// Fetches and fans out the per-building signals `config-setup-lens.ts` reduces into the four
// Konfiguraatio portfolio situation-card buckets and per-building tile status
// (docs/spec/concepts.md §2.4 → Konfiguraatio portfolio-scope IA).
//
// Scope (kanta vs salkku, plus city/type) is `useFilteredBuildings` — the same helper Tapahtumaloki
// / Havainnot / Suositukset already use (`lib/inventory-scope.ts`) — so switching ScopeBar changes
// this hook's building set exactly like every other portfolio surface; no new scoping logic is
// introduced here. Per-building completeness/services reads are fanned out via `feed-load.ts`'s
// `loadPerBuildingProgressive`: one building's failure or latency never blanks the page, and
// buckets/tiles/pins fill in as each building settles rather than waiting on the slowest.
import { useEffect, useMemo, useState } from "react";

import { loadPerBuildingProgressive } from "@/components/control-tower/feed-load";
import {
  getBuildingCompleteness,
  type BuildingCompleteness,
} from "@/lib/api/agent";
import {
  listBuildingServices,
  type Building,
  type BuildingService,
} from "@/lib/api/building";
import { useBuildings } from "@/lib/api/useBuildings";
import { useFilteredBuildings } from "@/lib/inventory-scope";
import { TAB_ICON, TAB_LABEL } from "@/lib/config-status-dimensions";
import {
  deriveBuildingSetup,
  SETUP_QUESTION_ORDER,
} from "@/lib/config-setup-lens";
import {
  deriveOverviewBuckets,
  type OverviewLensState,
  type PerBuildingOverview,
} from "@/lib/overview-lens";

interface BuildingSetupRow {
  building: Building;
  completeness: BuildingCompleteness | null;
  services: BuildingService[] | null;
}

// Each half of a building's setup data is caught independently — the completeness endpoint
// failing does not also blank out that building's (unrelated) service-activation state, and vice
// versa. `loadPerBuildingProgressive` never sees a rejection here, only a possibly-partial row.
async function fetchOne(building: Building): Promise<BuildingSetupRow[]> {
  const [completeness, services] = await Promise.all([
    getBuildingCompleteness(building.id).catch(
      (): BuildingCompleteness | null => null,
    ),
    listBuildingServices(building.id).catch(
      (): BuildingService[] | null => null,
    ),
  ]);
  return [{ building, completeness, services }];
}

/** Setup-lens data for the Konfiguraatio portfolio page. Re-fetches whenever the scoped building
 *  set changes (salkku selection, city/type filter, or the underlying inventory). */
export function useConfigSetupLens(): OverviewLensState {
  const inventory = useBuildings();
  const scopedBuildings = useFilteredBuildings(
    inventory.status === "ok" ? inventory.buildings : [],
  );
  // Captures the scoped building-id set; `scopedBuildings`' array identity churns every render.
  const scopeKey = scopedBuildings.map((b) => b.id).join("|");

  const [rows, setRows] = useState<BuildingSetupRow[]>([]);
  const [settledCount, setSettledCount] = useState(0);

  useEffect(() => {
    setRows([]);
    setSettledCount(0);
    if (scopedBuildings.length === 0) return;
    const collected: BuildingSetupRow[] = [];
    const { cancel } = loadPerBuildingProgressive(
      scopedBuildings,
      fetchOne,
      (settled, meta) => {
        collected.push(...settled);
        setRows([...collected]);
        setSettledCount(meta.settledCount);
      },
    );
    return cancel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  const perBuilding = useMemo(() => {
    const map = new Map<string, PerBuildingOverview>();
    for (const row of rows) {
      map.set(
        row.building.id,
        deriveBuildingSetup(row.building.id, row.completeness, row.services),
      );
    }
    return map;
  }, [rows]);

  const buckets = useMemo(
    () =>
      deriveOverviewBuckets(
        scopedBuildings,
        perBuilding,
        SETUP_QUESTION_ORDER,
        (key) => ({ label: TAB_LABEL[key], icon: TAB_ICON[key], tone: "warn" }),
      ),
    [scopedBuildings, perBuilding],
  );

  return {
    loading: settledCount < scopedBuildings.length,
    settledCount,
    totalCount: scopedBuildings.length,
    scopedBuildings,
    buckets,
    perBuilding,
  };
}
